import React from "react"

import { Image, Text } from "@/elements"
import clsx from "clsx"

export type AvatarSize = "3xs" | "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl"

type AvatarProps = {
  size?: AvatarSize
  initials?: string | null
  src?: string | null
  loading?: boolean
  badgeText?: string
  className?: string
  redacted?: boolean
}

const getSize = (size: AvatarSize) => {
  let sizeClass = ""
  let flexBasis = ""
  let fontSize = ""

  switch (size) {
    case "3xs":
      sizeClass = "size-[17px]"
      flexBasis = "basis-[17px]"
      fontSize = "text-[9px]"
      break
    case "2xs":
      sizeClass = "size-[20px]"
      flexBasis = "basis-[20px]"
      fontSize = "text-[9px]"
      break
    case "xs":
      sizeClass = "size-[24px]"
      flexBasis = "basis-[24px]"
      fontSize = "text-[9px]"
      break
    case "sm":
      sizeClass = "size-[32px]"
      flexBasis = "basis-[32px]"
      fontSize = "text-[12px]"
      break
    case "md":
      sizeClass = "size-[40px]"
      flexBasis = "basis-[40px]"
      fontSize = "text-[16px]"
      break
    case "xl":
      sizeClass = "size-[56px]"
      flexBasis = "basis-[56px]"
      fontSize = "text-[20px]"
      break
    case "2xl":
      sizeClass = "size-[80px]"
      flexBasis = "basis-[80px]"
      fontSize = "text-[24px]"
      break
    case "3xl":
      sizeClass = "size-[100px]"
      flexBasis = "basis-[100px]"
      fontSize = "text-[24px]"
      break
    case "4xl":
      sizeClass = "size-[120px]"
      flexBasis = "basis-[120px]"
      fontSize = "text-[24px]"
      break
    default:
    case "lg":
      sizeClass = "size-[48px]"
      flexBasis = "basis-[48px]"
      fontSize = "text-[16px]"
      break
  }

  return {
    fontSize,
    sizeClass,
    flexBasis,
  }
}

export const Avatar: React.FC<AvatarProps> = ({
  size = "md",
  initials = "",
  src,
  loading = false,
  badgeText,
  className,
  redacted = false,
}) => {
  const { sizeClass, flexBasis, fontSize } = getSize(size)

  return (
    <div className={clsx("relative", sizeClass, className)}>
      <div
        className={clsx(
          "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full transition-opacity duration-200",
          flexBasis,
          sizeClass,
          loading ? "bg-bg-2" : "bg-bg-3"
        )}>
        {src ? (
          <Image
            srcSetUrls={{
              sm: src,
              md: src,
              lg: src,
              xl: src,
            }}
            alt={`${initials ? `${initials} ` : ""} Avatar`}
            title={`${initials ? `${initials} ` : ""} Avatar`}
            className={"h-full w-full object-cover"}
          />
        ) : (
          <span className={clsx("text-center uppercase", fontSize, redacted ? "blur-[3px]" : "")}>{initials}</span>
        )}
      </div>
      {Boolean(badgeText) && (
        <div className="absolute bottom-[-18px] left-1/2 z-10 -translate-x-1/2 transform">
          <Text variant="label" className="rounded-sm bg-bg-2 p-2">
            {badgeText}
          </Text>
        </div>
      )}
    </div>
  )
}

export default Avatar
